import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader"
import DateFilterDropdown from "./DateFilterDropdown"
import analyticsService from "../../services/AnalyticsService"
import { TrendingUp, TrendingDown, IndianRupee, ArrowLeft, PieChart } from "lucide-react"
import Button from '../common/Button.js'
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import { hasAttendanceAccess } from '../../utils/networkMode.js';
import {
  AnalyticsStatCard,
  AnalyticsTable,
  analyticsStatsGrid4Class,
  useAnalyticsCompanyScope,
  buildAnalyticsParams,
} from './common/index.js';
import { formatCurrencyWhole as formatCurrency } from '../../utils/formatCurrency.js';

const hasPositiveAmount = (...values) =>
  values.some((v) => Number(v || 0) > 0);

const ProfitLossAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [filter, setFilter] = useState("current_month")
  const [customDates, setCustomDates] = useState({ from_date: "", to_date: "" })
  const {
    currentUser,
    companies,
    selectedCompany,
    handleCompanyChange,
    ready,
  } = useAnalyticsCompanyScope({ filter, customDates })

  const role = user?.role_name || user?.role
  const isSysAdmin = role === 'System Admin'
  const companyAccess = user?.company_features || user?.company_can_access || {}
  const hasLoansAccess = isSysAdmin || Boolean(companyAccess.loans)
  const hasExpensesAccess = isSysAdmin || Boolean(companyAccess.expenses)
  const hasSalaryAccess = hasAttendanceAccess(user)

  useEffect(() => {
    if (ready) fetchData()
  }, [filter, selectedCompany, ready])

  const fetchData = async () => {
    setLoading(true)
    try {
      const params = buildAnalyticsParams({
        filter,
        customDates,
        selectedCompany,
        currentUser,
      })
      const response = await analyticsService.getProfitLoss(params)
      setData(response)
    } catch (error) {
      console.error("Error fetching profit/loss analytics:", error)
    } finally {
      setLoading(false)
    }
  }

  const StatCard = AnalyticsStatCard;

  const loansRecovered = data?.revenue_breakdown?.loans_recovered
  const salaries = data?.expense_breakdown?.salaries
  const officeExpenses = data?.expense_breakdown?.office_expenses
  const loanRepayments = data?.expense_breakdown?.loan_repayments
  const refunds = data?.expense_breakdown?.refunds

  const showLoansRecovered =
    hasLoansAccess &&
    loansRecovered &&
    hasPositiveAmount(
      loansRecovered.total_given,
      loansRecovered.received_back,
      loansRecovered.pending_recovery,
      loansRecovered.loan_count,
    )

  const showSalaries =
    hasSalaryAccess &&
    salaries &&
    hasPositiveAmount(
      salaries.total_salary,
      salaries.paid_salary,
      salaries.pending_salary,
      salaries.employee_count,
    )

  const showOfficeExpenses =
    hasExpensesAccess &&
    officeExpenses &&
    hasPositiveAmount(
      officeExpenses.total_expenses,
      officeExpenses.paid_expenses,
      officeExpenses.pending_expenses,
      officeExpenses.expense_count,
    )

  const showLoanRepayments =
    hasLoansAccess &&
    loanRepayments &&
    hasPositiveAmount(
      loanRepayments.total_received,
      loanRepayments.paid_back,
      loanRepayments.pending_payment,
      loanRepayments.loan_count,
    )

  const showRefunds =
    refunds &&
    hasPositiveAmount(refunds.total_refunded, refunds.refund_count)

  const showExpenseBreakdown =
    showSalaries || showOfficeExpenses || showLoanRepayments || showRefunds

  if (loading) {
    return (
      <DashboardLayout fullWidth>
        <Loader fullScreen={false} size="default" message="Loading Profit & Loss Analytics..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout fullWidth>
              <div className="p-4 lg:p-6 w-full">
                {/* Header with Date Filter */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-semibold text-secondary">Profit & Loss Report</h1>
                    <p className="text-sm text-gray-600">Comprehensive financial overview</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <DateFilterDropdown
                      filter={filter}
                      setFilter={setFilter}
                      customDates={customDates}
                      setCustomDates={setCustomDates}
                      onApply={fetchData}
                      dateRange={data?.filter}
                      currentUser={currentUser}
                      companies={companies}
                      selectedCompany={selectedCompany}
                      onCompanyChange={handleCompanyChange}
                      showCompanyFilter
                    />
                    <Button variant="primary" icon={ArrowLeft} onClick={() => navigate("/analytics")}>Back</Button>
                  </div>
                </div>

                {/* Main Summary */}
                {data && (
                  <>
                    <div className={`${analyticsStatsGrid4Class} mb-6`}>
                      <StatCard
                        title="Total Revenue"
                        value={formatCurrency(data.summary.total_revenue)}
                        subtitle={`Collected: ${formatCurrency(data.summary.collected_revenue ?? data.revenue_breakdown?.shipments?.collected)}`}
                        icon={IndianRupee}
                        color="bg-emerald-100 text-emerald-600"
                      />
                      <StatCard
                        title="Total Expenses"
                        value={formatCurrency(data.summary.total_expenses)}
                        subtitle="Total expenditure"
                        icon={IndianRupee}
                        color="bg-rose-100 text-rose-600"
                      />
                      <StatCard
                        title="Net Profit/Loss"
                        value={formatCurrency(data.summary.net_profit_loss)}
                        subtitle={data.summary.status === "profit" ? "Profit" : "Loss"}
                        icon={data.summary.status === "profit" ? TrendingUp : TrendingDown}
                        color={data.summary.status === "profit" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}
                      />
                      <StatCard
                        title="Profit Margin"
                        value={`${data.summary.profit_margin_percentage.toFixed(2)}%`}
                        subtitle="Revenue to profit ratio"
                        icon={PieChart}
                        color="bg-purple-100 text-purple-600"
                      />
                    </div>

                    {/* Revenue Breakdown */}
                    <div className={`grid grid-cols-1 ${showExpenseBreakdown ? 'lg:grid-cols-2' : ''} gap-6 mb-6`}>
                      <div className="bg-white rounded-xl shadow-md p-4 lg:p-6">
                        <div className="flex items-center mb-4">
                          <TrendingUp className="w-6 h-6 text-green-500 mr-2" />
                          <h2 className="text-lg font-semibold text-gray-800">Revenue Breakdown</h2>
                        </div>

                        <div className="space-y-4">
                          {/* Shipments */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">{t('pages.shipment.title')}</h3>
                            <div className="bg-green-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Revenue:</span>
                                <span className="text-sm font-semibold text-green-600">
                                  {formatCurrency(data.revenue_breakdown.shipments.total_revenue)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Collected:</span>
                                <span className="text-sm font-semibold text-emerald-600">
                                  {formatCurrency(data.revenue_breakdown.shipments.collected)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Pending:</span>
                                <span className="text-sm font-semibold text-orange-600">
                                  {formatCurrency(data.revenue_breakdown.shipments.pending)}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 pt-1 border-t border-green-100">
                                <span>Shipment Count:</span>
                                <span>{data.revenue_breakdown.shipments.shipment_count}</span>
                              </div>
                            </div>
                          </div>

                          {/* Loans Recovered — only when loans access is enabled and data exists */}
                          {showLoansRecovered && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Loans Recovered</h3>
                            <div className="bg-blue-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Given:</span>
                                <span className="text-sm font-semibold text-gray-800">
                                  {formatCurrency(loansRecovered.total_given)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Received Back:</span>
                                <span className="text-sm font-semibold text-blue-600">
                                  {formatCurrency(loansRecovered.received_back)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Pending Recovery:</span>
                                <span className="text-sm font-semibold text-orange-600">
                                  {formatCurrency(loansRecovered.pending_recovery)}
                                </span>
                              </div>
                            </div>
                          </div>
                          )}

                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">By Payment Mode</h3>
                            <div className="space-y-2">
                              {Object.entries(data.revenue_breakdown.by_payment_mode).length > 0 ? (
                                Object.entries(data.revenue_breakdown.by_payment_mode).map(([mode, stats]) => (
                                  <div key={mode} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-sm text-gray-700 capitalize">{mode.replace(/_/g, " ")}</span>
                                    <div className="text-right">
                                      <p className="text-sm font-semibold text-gray-800">{formatCurrency(stats.amount)}</p>
                                      <p className="text-xs text-gray-500">{stats.count} shipments</p>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="p-2 bg-gray-50 rounded text-sm text-gray-500">No data available</div>
                              )}
                            </div>
                          </div>

                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">By Status</h3>
                            <div className="space-y-2">
                              {Object.entries(data.revenue_breakdown.by_status).length > 0 ? (
                                Object.entries(data.revenue_breakdown.by_status).map(([status, stats]) => (
                                  <div key={status} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                    <span className="text-sm text-gray-700 capitalize">{status.replace(/_/g, " ")}</span>
                                    <div className="text-right">
                                      <p className="text-sm font-semibold text-gray-800">{formatCurrency(stats.amount)}</p>
                                      <p className="text-xs text-gray-500">{stats.count} shipments</p>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="p-2 bg-gray-50 rounded text-sm text-gray-500">No data available</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expense Breakdown — only when at least one section has access + data */}
                      {showExpenseBreakdown && (
                      <div className="bg-white rounded-xl shadow-md p-4 lg:p-6">
                        <div className="flex items-center mb-4">
                          <TrendingDown className="w-6 h-6 text-red-500 mr-2" />
                          <h2 className="text-lg font-semibold text-gray-800">Expense Breakdown</h2>
                        </div>

                        <div className="space-y-4">
                          {/* Salaries — attendance access + non-zero data */}
                          {showSalaries && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Salaries</h3>
                            <div className="bg-red-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Salary:</span>
                                <span className="text-sm font-semibold text-gray-800">
                                  {formatCurrency(salaries.total_salary)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Paid:</span>
                                <span className="text-sm font-semibold text-red-600">
                                  {formatCurrency(salaries.paid_salary)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Pending:</span>
                                <span className="text-sm font-semibold text-orange-600">
                                  {formatCurrency(salaries.pending_salary)}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs text-gray-500 pt-1 border-t border-red-100">
                                <span>Employees:</span>
                                <span>{salaries.employee_count}</span>
                              </div>
                            </div>
                          </div>
                          )}

                          {/* Office Expenses — expenses access + non-zero data */}
                          {showOfficeExpenses && (
                            <div>
                              <h3 className="text-sm font-medium text-gray-700 mb-2">Office Expenses</h3>
                              <div className="bg-purple-50 p-3 rounded space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">Total Expenses:</span>
                                  <span className="text-sm font-semibold text-gray-800">
                                    {formatCurrency(officeExpenses.total_expenses)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">Paid:</span>
                                  <span className="text-sm font-semibold text-purple-600">
                                    {formatCurrency(officeExpenses.paid_expenses)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">Pending:</span>
                                  <span className="text-sm font-semibold text-orange-600">
                                    {formatCurrency(officeExpenses.pending_expenses)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs text-gray-500 pt-1 border-t border-purple-100">
                                  <span>Expense Count:</span>
                                  <span>{officeExpenses.expense_count}</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Loan Repayments — loans access + non-zero data */}
                          {showLoanRepayments && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Loan Repayments</h3>
                            <div className="bg-blue-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Received:</span>
                                <span className="text-sm font-semibold text-gray-800">
                                  {formatCurrency(loanRepayments.total_received)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Paid Back:</span>
                                <span className="text-sm font-semibold text-orange-600">
                                  {formatCurrency(loanRepayments.paid_back)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Pending Payment:</span>
                                <span className="text-sm font-semibold text-red-600">
                                  {formatCurrency(loanRepayments.pending_payment)}
                                </span>
                              </div>
                            </div>
                          </div>
                          )}

                          {/* Refunds — only when refund amount/count > 0 */}
                          {showRefunds && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Refunds</h3>
                            <div className="bg-yellow-50 p-3 rounded space-y-1">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">Total Refunded:</span>
                                <span className="text-sm font-semibold text-yellow-600">
                                  {formatCurrency(refunds.total_refunded)}
                                </span>
                              </div>
                              <div className="flex justify-between text-xs text-gray-500">
                                <span>Refund Count:</span>
                                <span>{refunds.refund_count}</span>
                              </div>
                            </div>
                          </div>
                          )}
                        </div>
                      </div>
                      )}
                    </div>

                    {/* Outstanding */}
                    <div className="bg-white rounded-xl shadow-md p-4 lg:p-6 mb-6">
                      <h2 className="text-lg font-semibold text-gray-800 mb-4">Outstanding Amounts</h2>
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="p-4 bg-blue-50 rounded-xl">
                          <p className="text-sm text-gray-600 mb-1">Receivables</p>
                          <p className="text-2xl font-bold text-blue-600">{formatCurrency(data.outstanding.receivables)}</p>
                          <p className="text-xs text-gray-500 mt-1">Amount to be received</p>
                        </div>
                        <div className="p-4 bg-red-50 rounded-xl">
                          <p className="text-sm text-gray-600 mb-1">Payables</p>
                          <p className="text-2xl font-bold text-red-600">{formatCurrency(data.outstanding.payables)}</p>
                          <p className="text-xs text-gray-500 mt-1">Amount to be paid</p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-xl">
                          <p className="text-sm text-gray-600 mb-1">Net Outstanding</p>
                          <p
                            className={`text-2xl font-bold ${data.outstanding.net_outstanding >= 0 ? "text-green-600" : "text-red-600"}`}
                          >
                            {formatCurrency(data.outstanding.net_outstanding)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {data.outstanding.net_outstanding >= 0 ? "In favor" : "In deficit"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Monthly Trend */}
                    {data.monthly_trend && Object.keys(data.monthly_trend).length > 0 && (
                      <AnalyticsTable
                        title="Monthly Trend"
                        columns={[
                          { header: 'Month', align: 'left' },
                          { header: 'Revenue', align: 'right', cellClassName: 'text-green-600' },
                          { header: 'Salary', align: 'right' },
                          { header: 'Office', align: 'right' },
                          { header: 'Total Expense', align: 'right', cellClassName: 'text-red-600' },
                          { header: 'Profit/Loss', align: 'right' },
                        ]}
                        rows={Object.entries(data.monthly_trend).map(([month, trend]) => ({
                          key: month,
                          cells: [
                            month,
                            formatCurrency(trend.revenue),
                            formatCurrency(trend.salary_expense),
                            formatCurrency(trend.office_expense),
                            formatCurrency(trend.total_expense),
                            <span key="profit" className={`font-bold ${trend.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatCurrency(trend.profit)}
                            </span>,
                          ],
                        }))}
                        tableMinWidth="720px"
                      />
                    )}
                  </>
                )}
              </div>
    </DashboardLayout>
  )
}

export default ProfitLossAnalytics;